import { ReactNode, useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";

interface PullToRefreshProps {
  children: ReactNode;
  onRefresh?: () => Promise<void>;
}

export const PullToRefresh = ({ children, onRefresh }: PullToRefreshProps) => {
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [startY, setStartY] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  
  const threshold = 80; // Pull distance needed to trigger refresh

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let scrollContainer: Element | null = null;

    const handleTouchStart = (e: TouchEvent) => {
      // Find the scrollable parent
      scrollContainer = container.querySelector('main') || container;
      const scrollTop = scrollContainer.scrollTop || window.scrollY;
      
      if (scrollTop === 0) {
        setStartY(e.touches[0].clientY);
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (startY === 0) return;
      
      const scrollTop = scrollContainer?.scrollTop || window.scrollY;
      if (scrollTop > 0) {
        setStartY(0);
        return;
      }

      const currentY = e.touches[0].clientY;
      const distance = currentY - startY;

      if (distance > 0 && distance < threshold * 2) {
        setPullDistance(distance);
        // Only prevent default when actively pulling
        if (distance > 5) {
          e.preventDefault();
        }
      }
    };

    const handleTouchEnd = async () => {
      if (pullDistance > threshold && !isRefreshing) {
        setIsRefreshing(true);
        
        if (onRefresh) {
          await onRefresh();
        } else {
          // Default behavior: reload the page
          window.location.reload();
        }
        
        setIsRefreshing(false);
      }
      
      setPullDistance(0);
      setStartY(0);
    };

    container.addEventListener("touchstart", handleTouchStart, { passive: true });
    container.addEventListener("touchmove", handleTouchMove, { passive: false });
    container.addEventListener("touchend", handleTouchEnd);

    return () => {
      container.removeEventListener("touchstart", handleTouchStart);
      container.removeEventListener("touchmove", handleTouchMove);
      container.removeEventListener("touchend", handleTouchEnd);
    };
  }, [startY, pullDistance, isRefreshing, onRefresh, threshold]);

  const pullProgress = Math.min(pullDistance / threshold, 1);
  const showIndicator = pullDistance > 10 || isRefreshing;

  return (
    <div ref={containerRef} className="relative flex-1 flex flex-col h-full overflow-y-auto">
      {showIndicator && (
        <div 
          className="absolute top-0 left-0 right-0 flex justify-center items-center transition-all duration-200 z-50"
          style={{ 
            height: `${Math.min(pullDistance, threshold)}px`,
            opacity: pullProgress 
          }}
        >
          <div className="bg-background/95 backdrop-blur rounded-full p-2 shadow-lg border border-border">
            <Loader2 
              className={`w-5 h-5 text-primary ${isRefreshing ? 'animate-spin' : ''}`}
              style={{ 
                transform: isRefreshing ? 'none' : `rotate(${pullProgress * 360}deg)` 
              }}
            />
          </div>
        </div>
      )}
      {children}
    </div>
  );
};
